<script>
import { nextTick } from "vue";
import { mapState, mapActions } from "vuex";
import MainModal from "@/package/components/MainModal.vue";
import MainCheckbox from "@/components/helpers/MainCheckbox.vue";
import MainButton from "@/components/helpers/MainButton.vue";
import DistribDoc from "@/modules/tariff/components/DistribDoc.vue";
import AgreeDoc from "@/modules/tariff/components/AgreeDoc.vue";
import { checkTariffBuyError } from "@/modules/tariff/helpers/check-tariff-buy-error";
import CertDocs from "@/modules/tariff/components/CertDocs.vue";

export default {
  components: {
    CertDocs,
    MainModal,
    MainCheckbox,
    MainButton,
    DistribDoc,
    AgreeDoc,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    certChecked: {
      type: Boolean,
      default: false,
    },
    tariff: {
      type: Object,
      default: () => ({}),
    },
    certificates: {
      type: Array,
      default: () => [],
    },

    selectedRepresentation: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      checkboxes: {
        agree: false,
        reviewed: false,
        distribAgree: false,
        certsAgree: false,
      },
      isAtBottom: false,
      step: 1,
      isLoading: false,
      isAtBottomFinish: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      cities: (state) => state.representation.cities,
      representations: (state) => state.representation.representations,
      previewTariffPrice: (state) => state.tariff.previewTariffPrice,
    }),

    isHasCerts() {
      return this.certificates.length > 0 && this.certChecked;
    },

    _modelValue: {
      get() {
        return this.value;
      },
      set(value) {
        if (!this.isLoading) {
          this.$emit("input", value);
        }
      },
    },

    isNextDisabled() {
      if (this.step === 1) {
        return !(
          this.isAtBottom &&
          this.checkboxes.agree &&
          this.checkboxes.reviewed
        );
      } else if (this.step === 2) {
        return !(this.isAtBottom && this.checkboxes.distribAgree);
      } else if (this.step === 3) {
        return !(this.isAtBottom && this.checkboxes.certsAgree);
      } else {
        return true;
      }
    },

    submitTitle() {
      if (this.step === 1) {
        return "Далее";
      } else if (this.step === 2 && this.isHasCerts) {
        return "Далее";
      } else {
        return "Оплатить";
      }
    },
  },
  watch: {
    _modelValue(opened) {
      if (opened) {
        nextTick(() => {
          this.onMainInfoScroll();
        });
      } else {
        this.isAtBottom = false;
        this.isAtBottomFinish = false;
        this.checkboxes.agree = false;
        this.checkboxes.reviewed = false;
        this.checkboxes.distribAgree = false;
        this.checkboxes.certsAgree = false;
      }
    },

    step() {
      this.isAtBottomFinish = false;
    },
  },
  methods: {
    ...mapActions({
      loadTariff: "tariff/loadTariff",
      buyTariff: "tariff/buyTariff",
      loadCities: "representation/loadCities",
      loadRepresentations: "representation/loadRepresentations",
      storeRepresentation: "representation/storeRepresentation",
      checkUser: "auth/checkUser",
      loadDocuments: "loadDocuments",
      createNotification: "createNotification",
    }),

    onMainInfoScroll() {
      const el = this.$refs.mainInfoRef;
      if (!el) return;
      const threshold = 4;
      this.isAtBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

      if (this.isAtBottom) {
        this.isAtBottomFinish = true;
      }
    },

    nextStep() {
      const el = this.$refs.mainInfoRef;

      if (this.step === 1) {
        this.step = 2;

        if (!el) return;
        el.scrollTop = 0;
      } else if (this.step === 2 && !this.isHasCerts) {
        this.buyTariffRequest();
      } else if (this.step === 2 && this.isHasCerts) {
        this.step = 3;
        if (!el) return;
        el.scrollTop = 0;
      } else if (this.step === 3) {
        this.buyTariffRequest();
      }
    },

    backButton() {
      if (this.step === 2) {
        this.step = 1;
      } else if (this.step === 3) {
        this.step = 2;
      } else {
        this._modelValue = false;
      }
    },

    async buyTariffRequest() {
      const tariffId = this.tariff.id;
      this.isLoading = true;

      try {
        if (!this.user.representation) {
          await this.storeRepresentation(this.selectedRepresentation.id);
        }

        await this.buyTariff({
          tariffId,
          certificates: this.certificates,
        });

        await this.checkUser();
        this.$emit("success");
      } catch (error) {
        const isModalOpen = checkTariffBuyError(error);

        if (isModalOpen) {
          this.$emit("changePrice");
          await this.loadTariff(tariffId);
        }
      } finally {
        this.isLoading = false;
        this._modelValue = false;
      }
    },
  },
};
</script>

<template>
  <MainModal :show-modal="_modelValue" @close="_modelValue = false">
    <div class="license-agreement-modal">
      <h3 v-if="step === 1">Лицензионный договор</h3>
      <h3 v-else-if="step === 2">Дистрибьюторское соглашение</h3>
      <h3 v-else-if="step === 3">Правила покупки и активации сертификата</h3>

      <p v-if="step === 1">
        Для продолжения покупки Вы должны принять условия лицензионного
        договора.

        <a href="https://esd.docs-lab.ru/document/download/104" download>
          Скачать текст документа
        </a>
      </p>

      <p v-else-if="step === 2">
        Для продолжения покупки Вы должны принять условия дистрибьюторского
        соглашения.
        <a href="https://esd.docs-lab.ru/document/download/4" download>
          Скачать текст документа
        </a>
      </p>
      <p v-else-if="step === 3">
        Для продолжения покупки Вы должны принять условия правил покупки и
        активации сертификата.
        <a href="https://esd.docs-lab.ru/document/download/136" download>
          Скачать текст документа
        </a>
      </p>

      <div
        ref="mainInfoRef"
        class="license-agreement-modal__main-info"
        @scroll="onMainInfoScroll"
      >
        <AgreeDoc v-if="step === 1" />

        <DistribDoc v-if="step === 2" />

        <CertDocs v-if="step === 3" />
      </div>

      <div class="license-agreement-modal__checkboxes-list">
        <MainCheckbox
          v-if="step === 1"
          id="agree"
          v-model="checkboxes.agree"
          :disabled="!isAtBottomFinish"
          required
          title="Я принимаю условия договора"
        />

        <MainCheckbox
          v-if="step === 1"
          id="reviewed"
          v-model="checkboxes.reviewed"
          :disabled="!isAtBottomFinish"
          required
          title="Я ознакомлен с тем, что, согласно пункту 4.8 Лицензионного договора, лицензионные платежи не подлежат перерасчету или возврату"
        />

        <MainCheckbox
          v-if="step === 2"
          id="distribAgree"
          v-model="checkboxes.distribAgree"
          :disabled="!isAtBottomFinish"
          required
          title="Я принимаю условия соглашения"
        />

        <MainCheckbox
          v-if="step === 3"
          id="certsAgree"
          v-model="checkboxes.certsAgree"
          required
          :disabled="!isAtBottomFinish"
          title="Я принимаю условия соглашения"
        />
      </div>

      <div class="license-agreement-modal__footer">
        <div class="btn-container">
          <MainButton title="Назад" color="outlined" @click="backButton" />

          <MainButton
            :title="submitTitle"
            color="dark"
            :disabled="isNextDisabled || isLoading"
            @click="nextStep"
          />
        </div>
      </div>
    </div>
  </MainModal>
</template>

<style lang="scss" scoped>
:deep(.main-modal__container) {
  padding: 48px !important;
  border-radius: 16px;
  background: var(--background-light-second, #f8f8f8);

  @media (max-width: 799px) {
    height: 100dvh;
  }
}

.license-agreement-modal {
  width: 1040px;
  height: calc(100dvh - 200px);
  display: grid;
  grid-template-rows: min-content min-content 1fr min-content min-content;
  row-gap: 24px;

  @media (max-width: 799px) {
    height: 100%;
  }

  > h3 {
    color: var(--text-dark-primary, #212121);

    @include title-3;
  }

  > p {
    color: var(--text-dark-primary, #212121);
    display: flex;
    flex-direction: column;

    @include body-1;

    > a {
      color: #2f80ed;
    }
  }

  &__main-info {
    overflow: auto;
    border-radius: 16px;
    padding: 16px 16px 16px 24px;
    background: var(--background-light-primary, #fff);
  }

  &__checkboxes-list {
    max-width: 600px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (max-width: 575px) {
      gap: 8px;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    > a {
      color: var(--other-link-blue, #2f80ed);
      display: flex;
      align-items: center;
      gap: 4px;
      @include body-1;

      > span {
        width: 24px;
        height: 24px;
        background: var(--other-link-blue, #2f80ed);
      }
    }

    .btn-container {
      display: grid;
      align-items: center;
      grid-template-columns: min-content min-content;
      gap: 16px;

      > button {
        flex-shrink: 0;
        padding: 12px 24px;

        @media (max-width: 575px) {
          height: 32px;
        }
      }
    }
  }
}

@media (max-width: 1439px) {
  .license-agreement-modal {
    width: calc(100dvw - 160px);
  }
}

@media (max-width: 899px) {
  .license-agreement-modal {
    padding-top: 0 !important;
  }
}

@media (max-width: 575px) {
  .license-agreement-modal {
    width: calc(100dvw - 80px);
    gap: 16px;

    &__footer {
      flex-direction: column;
      align-items: flex-start;
      gap: 20px;

      .btn-container {
        width: 100%;
        justify-content: flex-end;
      }
    }
  }
}

@media (max-width: 767px) {
  :deep(.main-modal__container) {
    padding: 24px 20px !important;
  }

  .license-agreement-modal {
    width: 100%;
    padding-top: 24px;
  }
}
</style>
